import React from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Zap, Cpu, Target, Rocket, ArrowRight } from "lucide-react";

export default function AboutOmoreGames() {

  const navigate = useNavigate();

  const values = [
    {
      icon: <Target className="w-6 h-6 text-blue-500" />,
      title: "Precision Design",
      desc: "We design mind games and reaction tests that measure focus, accuracy, and mental response speed."
    },
    {
      icon: <Zap className="w-6 h-6 text-yellow-500" />,
      title: "Instant Interaction",
      desc: "Our browser games are optimized for ultra-fast gameplay and real-time reflex, memory, and focus challenges."
    },
    {
      icon: <Cpu className="w-6 h-6 text-purple-500" />,
      title: "Cognitive Training",
      desc: "From reaction tests to memory and attention games, our experiences help improve cognitive performance."
    },
    {
      icon: <Rocket className="w-6 h-6 text-green-500" />,
      title: "Accessible Brain Games",
      desc: "Fast, lightweight browser games anyone can play instantly without downloads."
    }
  ];

  return (
    <div className="min-h-screen bg-[#090a0b] text-white selection:bg-blue-500/30 font-sans">

<Helmet>

  {/* BASIC SEO */}
  <html lang="en" />

  <title>
    About OMORE Games | Independent Brain Game Studio | Neuralis Reflex Test
  </title>

  <meta
    name="description"
    content="OMORE Games is an independent game studio creating brain training games, reaction time tests, and focus challenges like Neuralis. Built by Omkar More."
  />

  <meta
    name="keywords"
    content="
    omore games,
    neuralis game,
    brain game studio,
    reflex test game,
    reaction time test,
    focus training game,
    memory games online,
    indie game studio,
    browser brain games
    "
  />

  <meta name="robots" content="index, follow" />

  <meta name="viewport" content="width=device-width, initial-scale=1" />

  <link rel="canonical" href="https://omore.games/aboutomore" />


  {/* OPEN GRAPH */}
  <meta property="og:type" content="website" />
  <meta property="og:title" content="OMORE Games | Independent Brain Game Studio" />
  <meta
    property="og:description"
    content="Creator of Neuralis and other brain training games focused on reflex, focus, and mental speed."
  />
  <meta property="og:url" content="https://omore.games/aboutomore" />
  <meta property="og:site_name" content="OMORE Games" />

  <meta
    property="og:image"
    content="https://omore.games/og-cover.png"
  />


  {/* TWITTER */}
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content="OMORE Games Studio" />
  <meta
    name="twitter:description"
    content="Independent reflex game studio building Neuralis and other brain training games."
  />
  <meta
    name="twitter:image"
    content="https://omore.games/og-cover.png"
  />


  {/* ORGANIZATION SCHEMA */}
  <script type="application/ld+json">
    {JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "OMORE Games",
      url: "https://omore.games",
      logo: "https://omore.games/Logo.PNG",
      founder: {
        "@type": "Person",
        name: "Omkar More"
      }
    })}
  </script>


  {/* WEBSITE SCHEMA */}
  <script type="application/ld+json">
    {JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "OMORE Games",
      url: "https://omore.games"
    })}
  </script>


  {/* ABOUT PAGE SCHEMA */}
  <script type="application/ld+json">
    {JSON.stringify({
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "About OMORE Games",
      url: "https://omore.games/aboutomore",
      description:
        "OMORE Games is an independent reflex and brain training game studio created by Omkar More."
    })}
  </script>


</Helmet>

      {/* HERO */}
      <section className="relative pt-24 pb-16 md:pt-40 md:pb-24 px-6 overflow-hidden">

        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[300px] md:h-[500px] bg-blue-600/10 blur-[80px] md:blur-[120px] rounded-full" />

        <div className="max-w-4xl mx-auto relative z-10 text-center">

          <div className="mb-6 flex justify-center">
            <span className="text-[10px] md:text-xs tracking-[0.5em] md:tracking-[0.8em] text-blue-500 uppercase font-black px-4 py-1.5 border border-blue-500/20 bg-blue-500/5 rounded-full">
              Independent Reflex Game Studio
            </span>
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-7xl lg:text-9xl font-black tracking-tight uppercase mb-4 leading-none">
            OMORE <span className="text-zinc-800">GAMES</span>
          </h1>

          {/* NEW SUBTITLE */}
          <p className="text-[10px] tracking-[0.6em] text-zinc-600 uppercase font-bold mb-8">
            Interactive Reflex Game Studio
          </p>

          <div className="w-16 h-1 bg-blue-600 mx-auto mb-8" />

          <p className="text-zinc-400 text-base md:text-xl max-w-2xl mx-auto leading-relaxed px-4 font-medium">
            OMORE Games develops minimalist browser games focused on
            <strong> brain training, reaction time, memory improvement, and focus challenges</strong>.
            Our mission is to create fast and interactive experiences that help players
            test and improve their cognitive speed and mental performance.
          </p>

        </div>
      </section>


      {/* STORY */}
      <section className="py-20 px-6 bg-zinc-900/30">

        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12 md:gap-20 items-center">

          <div className="space-y-8">

            <h2 className="text-2xl md:text-3xl font-black uppercase tracking-tight leading-tight">
              Games Built Around <br />
              <span className="text-blue-500">Human Cognition</span>
            </h2>

            <div className="space-y-6 text-zinc-400 text-sm md:text-base leading-relaxed font-medium">

              <p>
                Founded by <span className="text-white font-bold">Omkar More</span>,
                OMORE Games focuses on creating high-performance web games
                that train reflexes, memory, attention, and decision-making ability.
              </p>

              <p>
                Instead of traditional entertainment, our games challenge
                reaction speed and mental focus to help players push the
                limits of cognitive performance.
              </p>

              <p>
                Our flagship experience,
                <span className="text-blue-500 font-bold"> Neuralis</span>,
                is a fast-paced reflex testing game that measures
                cognitive accuracy through real-time gameplay.
              </p>

            </div>

          </div>


          {/* PERFORMANCE */}
          <div className="relative group">

            <div className="absolute -inset-1 bg-blue-600 rounded-lg blur opacity-10 group-hover:opacity-20 transition duration-1000"></div>

            <div className="relative bg-[#0d0e10] border border-zinc-800 p-8 md:p-12 rounded-sm shadow-2xl">

              <div className="text-5xl md:text-7xl font-black text-white mb-2 tracking-tighter">
                0.08<span className="text-blue-500 text-2xl md:text-3xl">s</span>
              </div>

              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-600 font-black mb-10">
                Target Interaction Latency
              </div>

              <div className="space-y-4">

                <div className="flex justify-between items-center text-[10px] font-black uppercase tracking-widest text-zinc-400">
                  <span>Neural Sync</span>
                  <span>90%</span>
                </div>

                <div className="h-1 w-full bg-zinc-800 rounded-full overflow-hidden">
                  <div className="h-full w-[90%] bg-blue-600" />
                </div>

              </div>

            </div>

          </div>

        </div>

      </section>


      {/* VALUES */}
      <section className="py-24 px-6">

        <div className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">

          {values.map((val, i) => (

            <div
              key={i}
              className="p-8 border border-zinc-800 hover:border-blue-500 transition-colors bg-zinc-900/10 rounded-sm"
            >

              <div className="mb-6">{val.icon}</div>

              <h3 className="text-xs font-black uppercase tracking-[0.2em] mb-3">
                {val.title}
              </h3>

              <p className="text-[11px] md:text-xs text-zinc-200 leading-relaxed font-bold uppercase tracking-wide">
                {val.desc}
              </p>

            </div>

          ))}

        </div>

      </section>


      {/* CTA */}
      <section className="py-32 px-6 border-t border-zinc-900 text-center">

        <div className="max-w-3xl mx-auto space-y-10">

          <h2 className="text-4xl md:text-6xl font-black uppercase tracking-tighter leading-none">
            Test Your <span className="text-blue-600 underline decoration-zinc-800 underline-offset-8">
              Reaction Speed
            </span>
          </h2>

          <p className="text-zinc-200 font-medium max-w-lg mx-auto uppercase text-[10px] md:text-xs tracking-widest">
            Play Neuralis and challenge your reflexes through interactive brain games.
          </p>

          <button
            onClick={() => navigate("/sync/eyes")}
            className="group inline-flex items-center gap-6 px-12 py-6 bg-blue-600 text-white text-[10px] tracking-[0.5em] uppercase font-black hover:bg-blue-500 transition-all shadow-2xl shadow-blue-900/40 rounded-sm"
          >

            Launch Neuralis

            <ArrowRight className="w-4 h-4 group-hover:translate-x-2 transition-transform" />

          </button>

        </div>

      </section>

    </div>
  );

}